/* eslint-disable react/destructuring-assignment */
import React from 'react'
import { TargetsParam, animate } from 'animejs'
import Transition, { type TransitionStatus } from 'react-transition-group/Transition'
import { layering } from '@mobi/component-library/Foundations'
import { observeImmutable } from '../HOCs'
import { OverlayStyled } from './Overlay.styles'
import { type OverlayState, type OverlayStateRecord, state$ } from './OverlayDriver'
import { OverlayReset } from './signals'

const ANIMATION_DURATION = 150
const ANIMATION_OPACITY = { entering: [0, 1], exiting: [1, 0] }
const ANIMATION_TRANSLATEY = { entering: ['3rem', 0], exiting: [0, '3rem'] }

type ChildProps = {
  status: TransitionStatus
  layer: typeof layering.overlayLow | typeof layering.overlayHigh | typeof layering.modalOverlayHigh
  children: React.ReactNode
}

class OverlayChild extends React.Component<ChildProps> {
  private overlayRef = React.createRef<HTMLDivElement>()

  public componentDidUpdate() {
    const currentStatus = this.props.status

    if (currentStatus === 'entering' || currentStatus === 'exiting') {
      animate(this.overlayRef.current as TargetsParam, {
        opacity: {
          from: ANIMATION_OPACITY[currentStatus][0],
          to: ANIMATION_OPACITY[currentStatus][1],
          duration: ANIMATION_DURATION / 2,
        },
        translateY: {
          from: ANIMATION_TRANSLATEY[currentStatus][0],
          to: ANIMATION_TRANSLATEY[currentStatus][1],
        },
        ease: 'outQuart',
        duration: ANIMATION_DURATION,
        delay: 20,
      })
    }
  }

  public componentWillUnmount() {
    OverlayReset()
  }

  public render() {
    return (
      <OverlayStyled layer={this.props.layer} ref={this.overlayRef}>
        {this.props.children}
      </OverlayStyled>
    )
  }
}

export const OverlayComponent = ({ isOpen, Component, layer }: OverlayState): JSX.Element => (
  <Transition in={isOpen} timeout={ANIMATION_DURATION} unmountOnExit={true}>
    {status => {
      return (
        <OverlayChild layer={layer} status={status}>
          {Component && <Component />}
        </OverlayChild>
      )
    }}
  </Transition>
)

/** Used by Search */
export const Overlay = observeImmutable<OverlayStateRecord, object>(
  state$,
  ({ record: { Component, isOpen, layer } }) => (
    <OverlayComponent Component={Component} isOpen={isOpen} layer={layer} />
  )
)
